import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { saveAs } from 'file-saver';
import { generateGUID } from '../_helpers/guid.helper';
import { AssetTypeEnum } from '../enums/assetType.enum';
import { DocumentType } from '../enums/documentType.enum';
import { FileTypeEnum } from '../enums/fileType.enum';
import { ReportCategory } from '../enums/reportCategory.enum';
import { ReportFrequency } from '../enums/reportFrequency.enum';
import { FileAttachment } from '../models/fileAttachment.model';
import { Report } from '../models/report.model';
import { CreatePayload } from '../shared/grid/crud.service';
import { DateFormatService } from './dateFormat.service';
import { FileAttachmentService } from './file-attachment.service';
import { ReportService } from './report.service';
import { ToastService } from './toast.service';
import { UploadService } from './upload.service';
import { PdfGeneratedData } from '../models/pdfGeneratedData.model';
import { ENTITY_TYPES } from '../shared/widget-registry';
import { DOWNLOAD_BLOB_URL_REVOKE_DELAY_MS } from '../shared/utils/download.util';

export interface ReportGenerationOptions {
    entityIds: string[];
    entityType: string;
    organizationLevelId: string;
    organizationLevelName: string;
    metadataKey: string;
    identifier?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ReportGenerationService {
    constructor(
        private uploadService: UploadService,
        private fileAttachmentService: FileAttachmentService,
        private reportService: ReportService,
        private dateFormatService: DateFormatService,
        private toastService: ToastService
    ) { }

    async generateAndSaveReport(
        event: PdfGeneratedData,
        options: ReportGenerationOptions,
        openInNewTab: boolean = true,
        includeHtmlFile: boolean = false
    ): Promise<void> {
        let errorToReport: any = null;
        let fileAttachment: FileAttachment | undefined;

        try {
            const reportName = event.filename.replace(/\.(pdf|html)$/, '');
            const fileAttachments: FileAttachment[] = [];

            // Employee Letters (single-sample) save as Letters; every other path (incl. multi-sample) stays Report.
            // MUST branch on entityType here — both the letter path and the multi-sample path funnel through this method.
            const documentType = options.entityType === ENTITY_TYPES.EMPLOYEE_LETTER
                ? DocumentType.Letters
                : DocumentType.Report;

            if (includeHtmlFile && event.htmlContent) {
                const htmlFileKey = generateGUID();
                const htmlFilename = event.filename.replace('.pdf', '.html');
                const htmlBlob = new Blob([event.htmlContent], { type: 'text/html' });
                const htmlAttachment = await this.uploadBlobToS3(htmlBlob, htmlFileKey, htmlFilename, FileTypeEnum.Html, options.organizationLevelId, documentType);
                fileAttachments.push(htmlAttachment);
            }

            const pdfFileKey = generateGUID();
            const pdfAttachment = await this.uploadBlobToS3(event.blob, pdfFileKey, event.filename, FileTypeEnum.Pdf, options.organizationLevelId, documentType);
            fileAttachments.push(pdfAttachment);
            fileAttachment = pdfAttachment;

            const currentTimestamp = this.dateFormatService.convertDateToTimestamp(new Date()) * 1000;

            const report: CreatePayload<Report> = {
                name: reportName,
                category: ReportCategory.IndustrialHygiene,
                type: event.entityType,
                createdByUserEmail: '',
                createdDate: currentTimestamp,
                version: '1.0',
                frequency: ReportFrequency.Daily,
                organizationLevelId: options.organizationLevelId,
                organizationLevelName: options.organizationLevelName,
                autoGenerated: false,
                fileName: event.filename,
                reportUrl: '',
                templateId: event.template.id,
                templateJson: event.templateJson,
                metadata: {
                    [options.metadataKey]: options.entityIds
                },
                fileAttachments
            };

            await firstValueFrom(this.reportService.create(report));

            this.handleSuccessfulReportCreation(event.blob, event.filename, openInNewTab, options.entityType === ENTITY_TYPES.EMPLOYEE_LETTER);

        } catch (error) {
            this.toastService.displayErrorMessage('Failed to save report. Please try again', true);
            errorToReport = error;
        } finally {
            if (!errorToReport && event.onComplete) {
                event.onComplete(fileAttachment);
            } else if (errorToReport && event.onError) {
                event.onError(errorToReport);
            }
        }
    }

    private async uploadBlobToS3(
        blob: Blob,
        fileKey: string,
        filename: string,
        fileType: FileTypeEnum,
        organizationLevelId: string,
        documentType: DocumentType = DocumentType.Report
    ): Promise<FileAttachment> {
        try {
            const signedUrl = await firstValueFrom(this.uploadService.getAssetsPreSignedURL(fileKey, AssetTypeEnum.IHAttachments));

            const mimeType = fileType === FileTypeEnum.Html ? 'text/html' : 'application/pdf';
            const file = new File([blob], filename, { type: mimeType });

            await firstValueFrom(this.uploadService.uploadFile(signedUrl, file));

            const fileAttachment: CreatePayload<FileAttachment> = {
                name: filename,
                documentType: documentType,
                fileKey: fileKey,
                description: `${fileType === FileTypeEnum.Html ? 'HTML' : 'PDF'} report file`,
                organizationLevelId: organizationLevelId,
                fileSizeInBytes: blob.size,
                fileType: fileType
            };

            return await firstValueFrom(this.fileAttachmentService.create(fileAttachment));

        } catch (error) {
            const fileTypeLabel = fileType === FileTypeEnum.Html ? 'HTML' : 'PDF';
            throw new Error(`Failed to upload ${fileTypeLabel} report file`);
        }
    }

    private handleSuccessfulReportCreation(blob: Blob, filename: string, openInNewTab: boolean = true, isLetter: boolean = false): void {
        const blobUrl = URL.createObjectURL(blob);

        try {
            if (openInNewTab) {
                window.open(blobUrl, '_blank');
            }
            try {
                saveAs(blob, filename);
            } catch (e) {
                console.warn('File download failed (report was saved):', e);
            }
            this.toastService.showSuccess(isLetter ? 'Letter saved successfully' : 'Report saved successfully', true);
        } finally {
            // Defer the revoke so the newly-opened tab has time to read the blob;
            // revoking synchronously races the load and can yield an empty view.
            setTimeout(() => URL.revokeObjectURL(blobUrl), DOWNLOAD_BLOB_URL_REVOKE_DELAY_MS);
        }
    }
}
